import { useCallback, useMemo } from 'react'

import { useRouter } from 'next/navigation'

import {
  type GlobalHeaderProps,
  WatchlistDropdown,
} from '@b-stock/fe-core/bstock-react'

import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useWatchlistDropdownData } from './dropdownData/useWatchlistDropdownData'
import {
  type OpenDropdownState,
  useOpenDropdownToggle,
} from './useOpenDropdownState'

export function useWatchlist({
  personId,
  isAuthenticated,
  openDropdownState,
}: {
  personId: string | undefined
  isAuthenticated: boolean
  openDropdownState: OpenDropdownState
}) {
  const router = useRouter()
  const { trackButtonClicked } = useAnalytics()

  const { onOpenChange, isOpen } = useOpenDropdownToggle({
    key: 'watchlist',
    openDropdownState,
  })

  const watchlist = useWatchlistDropdownData(personId)

  const watchlistItemsWithAnalytics = useMemo(
    () =>
      watchlist.items.map((item, index) => ({
        ...item,
        onClick: () => {
          item.onClick?.()
          trackButtonClicked({
            buttonName: 'global_header_watchlist_clicked',
            listing_id: item.id,
            listing_position: index + 1,
          })
        },
      })),
    [watchlist.items, trackButtonClicked]
  )

  const handleWatchlistSeeAll = useCallback(() => {
    trackButtonClicked({
      buttonName: 'global_header_see_all_watchlist',
    })
    onOpenChange(false)
    router.push(watchlist.seeAllHref)
  }, [router, watchlist.seeAllHref, onOpenChange, trackButtonClicked])

  const content = useMemo(
    () =>
      isAuthenticated ? (
        <WatchlistDropdown
          items={watchlistItemsWithAnalytics}
          onSeeAll={handleWatchlistSeeAll}
        />
      ) : null,
    [isAuthenticated, watchlistItemsWithAnalytics, handleWatchlistSeeAll]
  )

  // Buyer-account features — only surface the icons to authenticated users so
  // logged-out visitors never see the dropdowns or fire their queries.
  const watchlistSlot = useMemo(
    () =>
      content
        ? {
            content,
            onOpenChange,
            isOpen,
          }
        : undefined,
    [content, onOpenChange, isOpen]
  )

  return {
    watchlistSlot,
  } satisfies Pick<GlobalHeaderProps, 'watchlistSlot'>
}
